import {useEffect, useRef, useState} from 'react'
import {Link, Navigate, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {ManualLayout} from '@/components/manual/manual-layout'
import {StepCard} from '@/components/manual/step-card'
import {ReplayPlayer, type ReplayPlayerHandle} from '@/components/manual/replay-player'
import {useManualSeo} from '@/components/manual/use-manual-seo'
import {Reveal} from '@/components/reveal'
import {Tag} from '@/components/ui/tag'
import {cn} from '@/lib/utils'
import {findSeccion, MANUAL_GRUPOS} from '@/manual'

/** Numeración corrida del manual, independiente del capítulo. */
const FLAT_SECCIONES = MANUAL_GRUPOS.flatMap((grupo) => grupo.secciones)
const FLAT_ORDER = FLAT_SECCIONES.map((s) => s.id)

/** Anterior/siguiente en el orden de lectura: en el teléfono el índice es un cajón cerrado. */
function SeccionPasos({flatIndex}: {flatIndex: number}) {
  const {t} = useTranslation()
  const anterior = flatIndex > 0 ? FLAT_SECCIONES[flatIndex - 1] : null
  const siguiente = flatIndex < FLAT_SECCIONES.length - 1 ? FLAT_SECCIONES[flatIndex + 1] : null
  if (!anterior && !siguiente) return null
  return (
    <nav className="mt-14 grid gap-3 border-t border-border pt-8 sm:grid-cols-2" aria-label={t('manual.pasos.aria')}>
      {anterior ? (
        <Link
          to={`/manual/${anterior.id}`}
          data-testid="manual-prev"
          className="surface surface-raised flex min-h-11 flex-col justify-center gap-1 p-4 outline-none focus-visible:ring-2 focus-visible:ring-ring/50">
          <span className="font-mono text-[10px] font-medium tracking-[0.12em] text-muted-foreground uppercase">
            ← {t('manual.pasos.anterior')}
          </span>
          <span className="font-heading text-sm text-foreground">{anterior.titulo}</span>
        </Link>
      ) : (
        <span className="hidden sm:block" aria-hidden="true" />
      )}
      {siguiente && (
        <Link
          to={`/manual/${siguiente.id}`}
          data-testid="manual-next"
          className="surface surface-raised flex min-h-11 flex-col justify-center gap-1 p-4 outline-none focus-visible:ring-2 focus-visible:ring-ring/50 sm:items-end sm:text-right">
          <span className="font-mono text-[10px] font-medium tracking-[0.12em] text-primary uppercase">
            {t('manual.pasos.siguiente')} →
          </span>
          <span className="font-heading text-sm text-foreground">{siguiente.titulo}</span>
        </Link>
      )}
    </nav>
  )
}

/** Sin replayId ni operaciones se muestra «en construcción» para que el índice aprobado sea navegable. */
export function ManualSeccionPage() {
  const {t} = useTranslation()
  const {seccionId} = useParams()
  const found = seccionId ? findSeccion(seccionId) : null
  const [activeStepId, setActiveStepId] = useState<string | null>(null)
  const stepRefs = useRef(new Map<string, HTMLDivElement>())
  const playerRef = useRef<ReplayPlayerHandle>(null)

  useManualSeo(found?.seccion.titulo ?? t('manual.title'), found?.seccion.resumen ?? t('manual.metaDescription'))

  useEffect(() => {
    if (!activeStepId) return
    const el = stepRefs.current.get(activeStepId)
    if (!el) return
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    el.scrollIntoView({behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest'})
  }, [activeStepId])

  if (!found) return <Navigate to="/manual" replace />
  const {grupo, seccion} = found
  const hasContent = seccion.operaciones.length > 0
  const flatIndex = FLAT_ORDER.indexOf(seccion.id)
  const chapterTitles = Object.fromEntries(
    seccion.operaciones.filter((op) => op.id).map((op) => [op.id as string, op.titulo])
  )

  return (
    <ManualLayout>
      <article data-testid="manual-seccion">
        <Reveal>
          <div className="flex items-baseline gap-3">
            <span className="font-mono text-sm font-semibold text-primary">
              {String(flatIndex + 1).padStart(2, '0')}
            </span>
            <p className="font-mono text-[10px] font-medium tracking-[0.14em] text-muted-foreground uppercase">
              {grupo.titulo}
            </p>
          </div>
          <h1 className="mt-2 font-heading text-3xl tracking-tight text-foreground sm:text-4xl">{seccion.titulo}</h1>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground sm:text-base">{seccion.resumen}</p>
        </Reveal>

        <div className={cn('mt-10', seccion.replayId && hasContent && 'lg:flex lg:items-start lg:gap-6')}>
          {seccion.replayId && (
            <Reveal delayMs={60} className={cn(hasContent && 'lg:sticky lg:top-20 lg:w-[62%] lg:shrink-0 xl:w-[66%]')}>
              <div className="overflow-hidden rounded-xl border border-border shadow-pop">
                <div className="flex items-center gap-1.5 border-b border-border bg-secondary/50 px-3 py-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-border" aria-hidden />
                  <span className="h-2.5 w-2.5 rounded-full bg-border" aria-hidden />
                  <span className="h-2.5 w-2.5 rounded-full bg-border" aria-hidden />
                  <span className="ml-2 font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
                    {seccion.titulo}
                  </span>
                </div>
                <ReplayPlayer
                  key={seccion.id}
                  ref={playerRef}
                  replayId={seccion.replayId}
                  title={seccion.titulo}
                  chapterTitles={chapterTitles}
                  onChapterChange={setActiveStepId}
                />
              </div>
            </Reveal>
          )}

          {hasContent ? (
            <div className={cn('mt-8 space-y-4', seccion.replayId && 'lg:mt-0 lg:min-w-0 lg:flex-1')}>
              {seccion.operaciones.map((op, i) => (
                <Reveal key={op.titulo} delayMs={(i % 6) * 40}>
                  <StepCard
                    op={op}
                    index={i}
                    active={!!op.id && op.id === activeStepId}
                    onSelect={
                      op.id && seccion.replayId ? () => playerRef.current?.seekToChapter(op.id as string) : null
                    }
                    cardRef={(el) => {
                      if (op.id) {
                        if (el) stepRefs.current.set(op.id, el)
                        else stepRefs.current.delete(op.id)
                      }
                    }}
                  />
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="surface mt-8 p-6" data-testid="manual-seccion-placeholder">
              <Tag tone="neutral">{t('manual.underConstructionTag')}</Tag>
              <p className="mt-3 max-w-xl text-sm text-muted-foreground">{t('manual.underConstruction')}</p>
            </div>
          )}
        </div>

        <SeccionPasos flatIndex={flatIndex} />
      </article>
    </ManualLayout>
  )
}
